import type {
  AccountType,
  ContentCreationConfig,
  ContentCreationConfigInput,
  Service,
  ServiceConfig,
  ServiceConfigInput,
} from '../types/account.js';

// SHARED SERVICE CATALOGUE + per-service config helpers (Phase 2b).
//
// Single source of the Service vocabulary, labels, the legacy `content` →
// `content_creation` read-normalise, and the type-varying escalation-contact
// roles. Reused by the account form, the read view, the accounts-grid filter,
// and the server zod enum so the list can't drift across surfaces.

// Ordered for the checklist (ads first, then the two configurable social
// services, then the rest). The zod enum + filter derive from this tuple.
export const SERVICE_VALUES = [
  'google_ads',
  'meta_ads',
  'content_creation',
  'community_management',
  'reporting',
  'meta_aia',
  'google_vla',
  'sms',
  'consulting',
  'email_marketing',
  'web_design',
] as const satisfies readonly Service[];

export const SERVICE_LABELS: Record<Service, string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  content_creation: 'Content Creation',
  community_management: 'Community Management',
  reporting: 'Reporting',
  meta_aia: 'Meta AIA (Automated Inventory Ads)',
  google_vla: 'Google VLA (Vehicle Listing Ads)',
  sms: 'SMS',
  consulting: 'Consulting',
  email_marketing: 'Email Marketing',
  web_design: 'Web Design',
};

export function serviceLabel(s: Service): string {
  return SERVICE_LABELS[s] ?? String(s).replace(/_/g, ' ');
}

// Read-normalise: the Phase-2b rename `content` → `content_creation`. A stored
// doc (pre-migration) or a legacy payload may still carry `content`; every read
// path maps it so nothing breaks before the migration runs. Unknown values pass
// through unchanged (defensive — an out-of-enum string is preserved, not
// dropped).
export function normalizeService(s: string): Service {
  return (s === 'content' ? 'content_creation' : s) as Service;
}

// Normalise + de-duplicate a services array (order-stable, keep-first). Applied
// at the read boundary (snapshotToAccount) so 'content' surfaces as
// 'content_creation' everywhere, and a doc that somehow carried both collapses
// to one.
export function normalizeServices(list: readonly string[]): Service[] {
  const out: Service[] = [];
  for (const raw of list) {
    const s = normalizeService(raw);
    if (!out.includes(s)) out.push(s);
  }
  return out;
}

// ---- Contact-details split read-normalise (2026-08-05) ----
//
// `contactDetailsForPost` became `salesContactDetailsForPost` + a new
// `serviceContactDetailsForPost`. Same posture as the billingAddress → address
// rename: the READ path falls back to the legacy field so the app works before
// AND after the migration, and the stored shape is normalised later.
//
// The legacy key is deliberately PRESERVED in the output (via the spread) — the
// import migration detects a not-yet-normalised doc by its presence. The wire
// schema's transform is what DROPS it, so every save writes the canonical shape.

/** Fold the legacy field into sales; default service to blank. Pure + total. */
export function normalizeContentCreationConfig(
  cc: ContentCreationConfigInput,
): ContentCreationConfig {
  return {
    ...cc,
    salesContactDetailsForPost:
      cc.salesContactDetailsForPost ?? cc.contactDetailsForPost ?? '',
    serviceContactDetailsForPost: cc.serviceContactDetailsForPost ?? '',
  };
}

/**
 * Normalise a stored serviceConfig at the read boundary (snapshotToAccount), so
 * every reader — form, view, caption wildcards — sees the canonical field names.
 * Returns the input untouched when there's no contentCreation block to fold.
 */
export function normalizeStoredServiceConfig(
  config: ServiceConfigInput | null | undefined,
): ServiceConfig | undefined {
  if (!config || typeof config !== 'object') return undefined;
  if (!config.contentCreation) return config as ServiceConfig;
  return { ...config, contentCreation: normalizeContentCreationConfig(config.contentCreation) };
}

/** True when a stored contentCreation block still carries the pre-split field. */
export function hasLegacyContactDetailsField(
  cc: ContentCreationConfigInput | null | undefined,
): boolean {
  return cc?.contactDetailsForPost !== undefined;
}

// ---- Community-management escalation roles (type-varying) ----
//
// The escalation-contact role set depends on the account type — the ONE
// documented type-driven variation (Scott, 2026-07-23):
//   Dealership          → Primary + Sales + Service
//   every other type    → Primary + Back Up
// Pure so the form + the read view render the same roles by construction.

export type EscalationRole = 'primary' | 'sales' | 'service' | 'backup';

export const ESCALATION_ROLE_LABELS: Record<EscalationRole, string> = {
  primary: 'Primary',
  sales: 'Sales',
  service: 'Service',
  backup: 'Back Up',
};

export function escalationRolesForAccountType(type: AccountType): EscalationRole[] {
  return type === 'dealership'
    ? ['primary', 'sales', 'service']
    : ['primary', 'backup'];
}

export function escalationRoleLabel(role: EscalationRole): string {
  return ESCALATION_ROLE_LABELS[role];
}

// ---- Services drive the operational gates (2026-07-24) ----
//
// Same derive-from-services pattern as Phase 2a (`runsMeta` ← `meta_ads`, see
// src/lib/platformEnabled.ts deriveRunFlagsFromServices). Ticking a service is
// what turns on its operational surface; the stored flag stays (all the
// ingestion gates read `account.communityEnabled`), only its WRITE path moved
// to the services array. Pure, so the server (create/update) and any client
// mirror derive identically.

// `communityEnabled` is DERIVED from the `community_management` service. Callers
// pass the already-normalised services array (snapshotToAccount normalises on
// read; the form emits canonical values), so a bare `.includes` is correct —
// but normalise defensively in case a raw/legacy `'content'`-style array slips
// through (community_management has no legacy alias, but the guard is cheap and
// keeps the helper safe for any services array).
export function deriveCommunityEnabledFromServices(services: readonly string[]): boolean {
  return normalizeServices(services).includes('community_management');
}
